import { useCallback, useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { reaction } from 'mobx';
import type { Query } from '@kentik/ui-shared/query/query.types';
import type { QueryHistoryEntry } from '@kentik/ui-shared/boards/panels';
import type BoardPanelModel from 'app/stores/boards/BoardPanelModel';

const MAX_HISTORY_ENTRIES = 50;

interface UseQueryHistoryOptions {
  panel: BoardPanelModel;
  summarize: (previousConfig: unknown, currentConfig: unknown) => Promise<string>;
  generateTitle: (query: Query) => Promise<string>;
  /** Called when an auto-title is generated, so the caller can sync it to the form */
  onAutoTitle?: (title: string) => void;
  /** Incremented when the panel's draft is discarded, to reload the saved history */
  discardSignal?: number;
}

export interface UseQueryHistoryResult {
  /** All history entries (newest first for display) */
  entries: QueryHistoryEntry[];
  /** Index of the currently active entry (0 = most recent) */
  headIndex: number;
  /** Whether we're viewing a non-HEAD entry */
  isViewingHistory: boolean;
  /** Navigate to a specific history entry. Returns the query to restore. */
  goToEntry: (index: number) => Query | null;
  /** Remove a single history entry by index. Cannot remove the active entry. */
  removeEntry: (index: number) => boolean;
  /** Discard all entries newer than `index`, making it the new HEAD. Returns the query to restore. */
  setToHead: (index: number) => Query | null;
  /** Whether the history panel is open */
  isOpen: boolean;
  /** Toggle the history panel */
  toggleOpen: () => void;
}

export function useQueryHistory({
  panel,
  summarize,
  generateTitle,
  onAutoTitle,
  discardSignal
}: UseQueryHistoryOptions): UseQueryHistoryResult {
  // Ensure the panel model always has a history array (prevents null in PUT requests)
  if (!Array.isArray(panel.get('history'))) {
    panel.set({ history: [] } as never);
  }

  const [entries, setEntries] = useState<QueryHistoryEntry[]>(() => panel.get('history') ?? []);
  const [headIndex, setHeadIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const headIndexRef = useRef(headIndex);
  headIndexRef.current = headIndex;

  // When restoring from history, skip recording the config change as a new entry
  const isRestoringRef = useRef(false);

  // Track the last auto-generated title so we can keep updating it on subsequent queries
  const lastAutoTitleRef = useRef<string>('');

  // Bumped on each discard to invalidate in-flight async work (e.g. title generation)
  // started for a query that has since been discarded.
  const discardEpochRef = useRef(0);

  const toggleOpen = useCallback(() => setIsOpen((prev) => !prev), []);

  /** Sync entries to state, ref, and panel atomically */
  const commitEntries = useCallback(
    (updated: QueryHistoryEntry[]) => {
      setEntries(updated);
      entriesRef.current = updated;
      panel.set({ history: updated } as never);
    },
    [panel]
  );

  /** Sync headIndex to state and ref atomically */
  const commitHead = useCallback((index: number) => {
    setHeadIndex(index);
    headIndexRef.current = index;
  }, []);

  /** Patch a single history entry matched by timestamp */
  const patchEntry = useCallback(
    (timestamp: string, patch: Partial<QueryHistoryEntry>) => {
      const latest = entriesRef.current;
      const idx = latest.findIndex((e) => e.timestamp === timestamp);
      if (idx >= 0) {
        const patched = [...latest];
        patched[idx] = { ...patched[idx], ...patch };
        commitEntries(patched);
      }
    },
    [commitEntries]
  );

  /** Begin a history restore: suppress new recordings and queue flag reset */
  const beginRestore = useCallback(() => {
    isRestoringRef.current = true;
    lastAutoTitleRef.current = panel.get('title') || '';
    queueMicrotask(() => {
      isRestoringRef.current = false;
    });
  }, [panel]);

  // MobX reaction: watch for panel config changes and auto-record history entries.
  // Since autoSave is debounced (300ms), this reaction fires as a microtask well
  // before the save — ensuring history is included in the save payload.
  useEffect(() => {
    let prevData: unknown = panel.get('config')?.data;
    let prevChart: unknown = panel.get('config')?.chart;
    let prevConfig: unknown = panel.get('config');

    const disposer = reaction(
      () => panel.get('config'),
      (config) => {
        const curr = config?.data as Query | undefined;
        const prev = prevData as Query | undefined;
        const currChart = config?.chart;
        const prevChartSnap = prevChart;
        const prevConfigSnap = prevConfig;
        prevData = curr;
        prevChart = currChart;
        prevConfig = config;

        if (isRestoringRef.current || panel.isDiscardingDraft) {
          return;
        }

        // Skip if current query has no metrics, or the entire config is identical
        if (!curr?.metrics?.length || isEqual(prevConfigSnap, config)) {
          return;
        }

        // If viewing an old entry, discard forward entries before recording
        const base = headIndexRef.current > 0 ? entriesRef.current.slice(headIndexRef.current) : entriesRef.current;

        const newEntry: QueryHistoryEntry = {
          timestamp: new Date().toISOString(),
          user_id: '',
          summary: 'Summarizing…',
          query: curr,
          chart: currChart,
          autoTitle: '',
          user_prompt: ''
        };

        commitEntries([newEntry, ...base].slice(0, MAX_HISTORY_ENTRIES));
        commitHead(0);

        // Async: generate summary — skip for the first entry (no meaningful diff)
        const summaryPromise = prev
          ? summarize({ data: prev, chart: prevChartSnap }, { data: curr, chart: currChart })
          : Promise.resolve('Initial query');

        summaryPromise
          .then((summary) => patchEntry(newEntry.timestamp, { summary }))
          .catch(() => patchEntry(newEntry.timestamp, { summary: 'Manual change' }));

        // Async: generate auto-title
        const titleEpoch = discardEpochRef.current;
        generateTitle(curr)
          .then((autoTitle) => {
            // Skip if the query was discarded while the title was being generated,
            // so a stale title can't re-create a draft after Cancel.
            if (discardEpochRef.current !== titleEpoch) {
              return;
            }
            patchEntry(newEntry.timestamp, { autoTitle });
            // Auto-title the panel if user hasn't manually set one,
            // or if the current title is still the previous auto-title (i.e. not manually edited)
            const currentTitle = panel.get('title') || '';
            if (autoTitle && (!currentTitle || currentTitle === lastAutoTitleRef.current)) {
              lastAutoTitleRef.current = autoTitle;
              panel.set({ title: autoTitle } as never);
              onAutoTitle?.(autoTitle);
            }
          })
          .catch(() => undefined);
      },
      { fireImmediately: false }
    );

    return disposer;
  }, [panel, summarize, generateTitle, onAutoTitle, commitEntries, commitHead, patchEntry]);

  const lastDiscardSignal = useRef(discardSignal);
  useEffect(() => {
    if (discardSignal === lastDiscardSignal.current) {
      return;
    }
    lastDiscardSignal.current = discardSignal;
    discardEpochRef.current += 1;
    commitEntries(panel.get('history') ?? []);
    commitHead(0);
  }, [discardSignal, panel, commitEntries, commitHead]);

  const goToEntry = useCallback(
    (index: number): Query | null => {
      if (index < 0 || index >= entriesRef.current.length) {
        return null;
      }
      commitHead(index);
      beginRestore();
      return entriesRef.current[index].query as Query;
    },
    [commitHead, beginRestore]
  );

  const removeEntry = useCallback(
    (index: number): boolean => {
      const { current } = entriesRef;
      if (index < 0 || index >= current.length || index === headIndexRef.current) {
        return false;
      }
      const updated = [...current];
      updated.splice(index, 1);
      // Adjust headIndex if a preceding entry was removed
      const newHeadIndex = index < headIndexRef.current ? headIndexRef.current - 1 : headIndexRef.current;
      commitEntries(updated);
      commitHead(newHeadIndex);
      return true;
    },
    [commitEntries, commitHead]
  );

  const setToHead = useCallback(
    (index: number): Query | null => {
      const { current } = entriesRef;
      if (index <= 0 || index >= current.length) {
        return null;
      }
      // Discard entries at indices 0..(index-1)
      const updated = current.slice(index);
      commitEntries(updated);
      commitHead(0);
      beginRestore();
      return (updated[0]?.query as Query) ?? null;
    },
    [commitEntries, commitHead, beginRestore]
  );

  return {
    entries,
    headIndex,
    isViewingHistory: headIndex > 0,
    goToEntry,
    removeEntry,
    setToHead,
    isOpen,
    toggleOpen
  };
}
